import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { AuthUser } from '../lib/types';

type AuthContextValue = {user: AuthUser | null; checking: boolean; error: string; signingIn: boolean;
  signIn: () => Promise<void>; signOut: () => Promise<void>; refresh: () => Promise<void>; complete: (user: AuthUser) => void};
const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({children}: {children: React.ReactNode}) {
  const location = useLocation();
  const navigate = useNavigate();
  const callback = new URLSearchParams(location.hash.slice(1)).has('session_id');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const verificationVersion = useRef(0);
  const verify = useCallback(async (signal?: AbortSignal) => {
    const version = ++verificationVersion.current;
    const isCurrent = () => !signal?.aborted && version === verificationVersion.current;
    try {
      const value = await api<AuthUser>('/auth/me', 'GET', undefined, signal);
      if (isCurrent()) {setUser(value); setError('');}
    } catch (error: any) {
      if (!isCurrent()) return;
      setUser(null);
      setError(error instanceof ApiError && error.status === 401 ? '' : 'We could not check your sign-in. Please retry.');
    } finally {if (isCurrent()) setChecking(false);}
  }, []);
  const refresh = useCallback(() => verify(), [verify]);
  const complete = useCallback((value: AuthUser) => {
    verificationVersion.current += 1;
    setUser(value); setChecking(false); setError('');
  }, []);

  useEffect(() => {
    // Let the one-time provider callback establish its cookie BEFORE /me runs.
    if (callback) return;
    const controller = new AbortController();
    void verify(controller.signal);
    return () => controller.abort();
  }, [callback, verify]);
  useEffect(() => {
    const check = () => {if (!callback) void refresh();};
    const onVisible = () => {if (document.visibilityState === 'visible') check();};
    window.addEventListener('triage-auth-changed', check);
    document.addEventListener('visibilitychange', onVisible);
    return () => {window.removeEventListener('triage-auth-changed', check); document.removeEventListener('visibilitychange', onVisible);};
  }, [callback, refresh]);

  const signIn = async () => {
    if (signingIn) return;
    setSigningIn(true); setError('');
    try {
      const {state} = await api<{state: string}>('/auth/start', 'POST');
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = `${window.location.origin}/?auth_state=${encodeURIComponent(state)}`;
      window.location.assign(`https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`);
    } catch (error: any) {setError(error.message); setSigningIn(false);}
  };
  const signOut = async () => {
    try {
      await api('/auth/logout', 'POST');
      // A /me response begun before revocation must never restore the old user.
      verificationVersion.current += 1;
      setUser(null); setError('');
      navigate('/', {replace: true});
    } catch {setError('Sign-out could not be completed. Please retry.');}
  };
  return <AuthContext.Provider value={{user, checking, error, signingIn, signIn, signOut, refresh, complete}}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('AuthProvider is required');
  return context;
}